import Search from "./Components/Search";
import HomePage from "./Components/HomePage";
import { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import LoginPage from "./LoginPage";
import Profile from "./Components/Profile";
import Post from "./Components/Post";
import Settings from "./Components/Settings";
import PostDetail from "./Components/PostDetail";
import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut
} from "firebase/auth";

import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, query, orderBy, onSnapshot, where, getDocs } from "firebase/firestore";
// PostList component to display posts
//  posts are fetched from Firestore and displayed for all users.
// posts persist in the database and are visible after log-in/log-out as long as the user is authenticated.
function PostList({ user }) {
    // Upvote/Downvote handlers
    const handleUpvote = async (post) => {
      if (!user || user.uid === post.userId) return;
      const postRef = doc(db, 'posts', post.id);
      const upvotedBy = post.upvotedBy || [];
      if (upvotedBy.includes(user.uid)) return; // already upvoted
      await updateDoc(postRef, {
        upvotes: (post.upvotes ?? 0) + 1,
        upvotedBy: [...upvotedBy, user.uid],
      });
    };

    const handleDownvote = async (post) => {
      if (!user || user.uid === post.userId) return;
      const postRef = doc(db, 'posts', post.id);
      const upvotedBy = post.upvotedBy || [];
      if (!upvotedBy.includes(user.uid)) return; // not upvoted
      await updateDoc(postRef, {
        upvotes: Math.max((post.upvotes ?? 1) - 1, 0),
        upvotedBy: upvotedBy.filter(uid => uid !== user.uid),
      });
    };
  const [posts, setPosts] = useState([]);
  const [usernames, setUsernames] = useState({});
  // Only filter by rounds and ratings
  const [selectedRounds, setSelectedRounds] = useState([]);
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const availableRounds = ['1', '2', '3', '4', '5+'];
  const availableRatings = ['1', '2', '3', '4', '5'];

  useEffect(() => {
    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let postDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // If not authenticated, only show the most recent 3 posts
      if (!user) {
        postDocs = postDocs.slice(0, 3);
      }
      setPosts(postDocs);
      const uniqueUserIds = Array.from(new Set(postDocs.map(post => post.userId)));
      uniqueUserIds.forEach(async (uid) => {
        if (!usernames[uid]) {
          const userRef = doc(db, 'users', uid);
          const snap = await getDoc(userRef);
          setUsernames(prev => ({ ...prev, [uid]: snap.exists() && snap.data().username ? snap.data().username : '' }));
        }
      });
    });
    return () => unsubscribe();
  }, [user, usernames]);

  // Filter logic (AND)
  const filterPost = (post) => {
    if (selectedRounds.length > 0 && !selectedRounds.includes(String(post.numRounds))) return false;
    if (selectedRatings.length > 0 && !selectedRatings.includes(String(post.rating))) return false;
    return true;
  };
  // Sort logic
  const sortPosts = (posts) => {
    if (sortBy === 'date') {
      return posts.slice().sort((a, b) => {
        const aDate = a.createdAt?.seconds || 0;
        const bDate = b.createdAt?.seconds || 0;
        return bDate - aDate;
      });
    } else if (sortBy === 'upvotes') {
      return posts.slice().sort((a, b) => (b.upvotes ?? 0) - (a.upvotes ?? 0));
    } else if (sortBy === 'lowupvotes') {
      return posts.slice().sort((a, b) => (a.upvotes ?? 0) - (b.upvotes ?? 0));
    }
    return posts;
  };
  // UI handlers
  const handleCheckboxChange = (value, selectedArray, setSelectedArray) => {
    if (selectedArray.includes(value)) {
      setSelectedArray(selectedArray.filter(item => item !== value));
    } else {
      setSelectedArray([...selectedArray, value]);
    }
  };
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };
  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  // Filter and sort posts
  const filteredSortedPosts = sortPosts(posts.filter(filterPost));

  return (
    <div style={{background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '24px', margin: '24px 0', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto'}}>
      <h2 style={{textAlign: 'center', marginBottom: '20px', color: '#333'}}>Recent Interview Posts</h2>
      {/* Filter and sort bar (match Search page) */}
      <div className="filter-sort-bar">
        <div className="filter-dropdown-container">
          <button type="button" className="filter-toggle-btn" onClick={toggleFilters}>
            Filters {filtersOpen ? '▲' : '▼'}
          </button>
          {filtersOpen && (
            <div className="filters-dropdown">
              <div className="filter-section">
                <h4>Number of Rounds</h4>
                <div className="checkbox-group">
                  {availableRounds.map((round) => (
                    <label key={round} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedRounds.includes(round)}
                        onChange={() => handleCheckboxChange(round, selectedRounds, setSelectedRounds)}
                      />
                      {round}
                    </label>
                  ))}
                </div>
              </div>
              <div className="filter-section">
                <h4>Rating</h4>
                <div className="checkbox-group">
                  {availableRatings.map((rating) => (
                    <label key={rating} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedRatings.includes(rating)}
                        onChange={() => handleCheckboxChange(rating, selectedRatings, setSelectedRatings)}
                      />
                      {rating} Star{rating !== '1' ? 's' : ''}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="sort-dropdown-container">
          <label htmlFor="sortBy">Sort by:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={handleSortChange}
            className="sort-select"
          >
            <option value="date">Newest</option>
            <option value="upvotes">Most Upvotes</option>
            <option value="lowupvotes">Lowest Upvotes</option>
          </select>
        </div>
      </div>
      {/* Posts r loaded from Firestore and persist across log-in/log-out */}
      {filteredSortedPosts.length === 0 && <p style={{textAlign: 'center', color: '#888'}}>No posts yet.</p>}
      <ul style={{listStyle: 'none', padding: 0}}>
        {filteredSortedPosts.map((post, idx) => (
          <li key={post.id} style={{border: '1px solid #e0e0e0', borderRadius: '8px', margin: '16px 0', padding: '16px', background: '#fafafa', boxShadow: '0 1px 4px rgba(0,0,0,0.04)'}}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              {/* Upvote Section */}
              <div className="vote-section" style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '16px'}}>
                <button
                  className={`vote-btn upvote`}
                  style={{border: 'none', background: 'none', cursor: (user && user.uid !== post.userId && !(post.upvotedBy || []).includes(user?.uid)) ? 'pointer' : 'not-allowed', fontSize: '1.2em', color: (user && user.uid !== post.userId && !(post.upvotedBy || []).includes(user?.uid)) ? '#888' : '#ccc'}}
                  disabled={!user || user.uid === post.userId || (post.upvotedBy || []).includes(user.uid)}
                  title={!user ? 'Sign in to upvote' : ''}
                  onClick={() => handleUpvote(post)}
                >
                  ▲
                </button>
                <span className="vote-count" style={{fontWeight: 'bold', color: '#333'}}>{post.upvotes ?? 0}</span>
                <button
                  className="vote-btn downvote"
                  style={{border: 'none', background: 'none', cursor: (user && user.uid !== post.userId && (post.upvotes ?? 0) > 0 && (post.upvotedBy || []).includes(user?.uid)) ? 'pointer' : 'not-allowed', fontSize: '1.2em', color: (user && user.uid !== post.userId && (post.upvotes ?? 0) > 0 && (post.upvotedBy || []).includes(user?.uid)) ? '#888' : '#eee'}}
                  disabled={!user || user.uid === post.userId || !(post.upvotedBy || []).includes(user.uid) || (post.upvotes ?? 0) === 0}
                  title={!user ? 'Sign in to downvote' : ''}
                  onClick={() => handleDownvote(post)}
                >
                  ▼
                </button>
              </div>
              <div style={{flex: 1}}>
                {user ? (
                  <Link 
                    to={`/post/${post.id}`}
                    state={{ from: window.location.pathname }}
                    style={{textDecoration: 'none', color: 'inherit', display: 'block'}}
                  >
                    <strong style={{fontSize: '1.1em', color: '#222'}}>{post.companyName}</strong> - <span style={{color: '#555'}}>{post.roleName}</span><br/>
                    <span style={{color: '#666'}}>Rounds: {post.numRounds} | Rating: {post.rating}</span><br/>
                    {/* Behavioral and Technical questions removed from homepage preview */}
                    <span style={{color: '#444'}}>Offer: {post.offerReceived ? 'Yes' : 'No'}</span><br/>
                    <span style={{color: '#888', fontSize: '0.9em'}}>By: {usernames[post.userId]}</span>
                  </Link>
                ) : (
                  <div style={{color: '#222'}}>
                    <strong style={{fontSize: '1.1em'}}>{post.companyName}</strong> - <span style={{color: '#555'}}>{post.roleName}</span><br/>
                    <span style={{color: '#666'}}>Rounds: {post.numRounds} | Rating: {post.rating}</span><br/>
                    {/* Hide questions for unauthenticated users */}
                    <span style={{
                      color: '#80B5D7',
                      background: '#E6DFE4',
                      fontStyle: 'italic',
                      padding: '2px 8px',
                      borderRadius: '6px',
                      display: 'inline-block',
                      margin: '4px 0'
                    }}>
                      Sign in to view behavioral and technical questions.
                    </span><br/>
                    <span style={{color: '#444'}}>Offer: {post.offerReceived ? 'Yes' : 'No'}</span><br/>
                    <span style={{color: '#888', fontSize: '0.9em'}}>By: {usernames[post.userId]}</span>
                    <div style={{marginTop: '8px', color: '#b00', fontWeight: 500, fontStyle: 'italic'}}>
                      Please <span style={{textDecoration: 'underline'}}>sign in</span> to see more details about this post.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [profile, setProfile] = useState({ username: "", school: "", major: "", gradYear: "" });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);


  // load profile for any logged-in user
  useEffect(() => {
    async function loadProfile() {
      if (!user) return;
      setProfileLoading(true);
      setProfileMessage("");
      setError("");
      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          setProfile({
            username: data.username || "",
            school: data.school || "",
            major: data.major || "",
            gradYear: data.gradYear || ""
          });
        } else {
          await setDoc(userRef, {
            email: user.email,
            username: "",
            school: "",
            major: "",
            gradYear: "",
            updatedAt: serverTimestamp()
          });
          setProfile({ username: "", school: "", major: "", gradYear: "" });
        }
      } catch (err) {
        errorHandled(err);
      } finally {
        setProfileLoading(false);
      }
    }
    loadProfile();
  }, [user]);

  async function signUp(email, password, username) {
    setError("");
    setSuccess(""); 
    const cleanEmail = email.trim();
    const cleanPassword = password;
    // Check username uniqueness
    const q = query(collection(db, "users"), where("username", "==", username.trim()));
    const snap = await getDocs(q);
    if (!username || username.trim().length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }
    if (!snap.empty) {
      setError("Username already taken. Please choose another.");
      return;
    }
    createUserWithEmailAndPassword(auth, cleanEmail, cleanPassword)
      .then(async function (result) {
        const uid = result.user.uid;
        await setDoc(doc(db, "users", uid), {
          email: result.user.email,
          username: username.trim(),
          school: "",
          major: "",
          gradYear: "",
          updatedAt: serverTimestamp()
        }, { merge: true });
        setEmail("");
        setPassword("");
        setSuccess("Account created successfully!");
        setTimeout(() => {
          setSuccess("");
          setShowLogin(false);
        },  3000);
      })
      .catch(errorHandled);
  }

  function signIn() {
    setError("");
    setSuccess(""); 
    const cleanEmail = email.trim();
    signInWithEmailAndPassword(auth, cleanEmail, password)
      .then(function () {
        setEmail("");
        setPassword("");
        setSuccess("Signed in successfully!");
        setTimeout(() => {
          setSuccess("");
          setShowLogin(false);
        },  3000);
      })
      .catch(errorHandled);
  }

  function signingOut() {
    signOut(auth)
      .catch(errorHandled);
  }

  async function saveProfile() {
    setProfileMessage("");
    setError("");
    const username = profile.username ? profile.username.trim() : "";
    const school = profile.school ? profile.school.trim() : "";
    const major = profile.major ? profile.major.trim() : "";
    const gradYear = String(profile.gradYear).trim();
    const email = profile.email ? profile.email.trim() : (user.email || "");
    // Validation
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setError("Valid email is required.");
      return;
    }
    if (!username || username.length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }
    if (!school || school.length < 2) {
      setError("School is required.");
      return;
    } else if (/^\d+$/.test(school)) {
      setError("School cannot be all numbers.");
      return;
    }
    if (!major || major.length < 2) {
      setError("Major is required.");
      return;
    } else if (/^\d+$/.test(major)) {
      setError("Major cannot be all numbers.");
      return;
    }
    if (!gradYear || !/^\d{4}$/.test(gradYear)) {
      setError("Graduation Year must be 4-digits.");
      return;
    }
    try {
      await updateDoc(doc(db, "users", user.uid), {
        email,
        username,
        school,
        major,
        gradYear,
        updatedAt: serverTimestamp()
      });
      setProfileMessage("Profile updated successfully.");
    } catch (err) {
      errorHandled(err);
    }
  }

  function errorHandled(err) {
    setError(err.message);
    console.log(err);
  }

  const [showLogin, setShowLogin] = useState(false);
  if (authLoading) {
    return <div style={{textAlign: 'center', marginTop: '2em'}}>Loading...</div>;
  }
  return (
    <Router>
      <div>
        {/* Navigation Bar - show to all users */}
        <nav className="navbar" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <ul className="nav-list" style={{display: 'flex', gap: '40px', margin: 0, padding: 0, alignItems: 'center'}}>
            <li style={{display: 'flex', alignItems: 'center'}}>
              <Link to="/" className="nav-link" style={{textDecoration: 'none'}}>
                [home]
              </Link>
            </li>
            <li style={{display: 'flex', alignItems: 'center'}}>
              <Link to="/search" className="nav-link" style={{textDecoration: 'none'}}>
                [search]
              </Link>
            </li>
            <li style={{display: 'flex', alignItems: 'center'}}>
              {user && (
                <Link to="/post" className="nav-link" style={{textDecoration: 'none'}}>
                  [post]
                </Link>
              )}
            </li>
          </ul>
          <div className="navbar-auth-info" style={{display: 'flex', alignItems: 'center', gap: '1em', marginLeft: 'auto'}}>
            {user ? (
              <>
                <Link to="/profile" className="nav-link" style={{textDecoration: 'none'}}>[profile]</Link>
                <button onClick={signingOut} style={{marginLeft: '1em'}}>[sign out]</button>
              </>
            ) : (
              <button onClick={() => setShowLogin(true)} style={{marginLeft: '1em', fontFamily: "'Consolas', monospace"}}>  [login]</button>
            )}
          </div>
        </nav>
        <div className="main-content">
          <Routes>
            <Route path="/" element={
              <>
                <HomePage user={user} setShowLogin={setShowLogin} />
                {user && <PostList user={user} />}
              </>
              } />
            <Route path="/search" element={<Search />} />
            <Route path="/post/:id" element={<PostDetail user={user} setShowLogin={setShowLogin} />} />
            {/* Only allow authenticated users to access these routes */}
            {user && <Route path="/post" element={<Post />} />}
            {user && <Route path="/profile" element={<Profile user={user} profile={profile} setProfile={setProfile} profileLoading={profileLoading} profileMessage={profileMessage} error={error} saveProfile={saveProfile} onSignOut={signingOut} />} />}
            {user && <Route path="/settings" element={<Settings user={user} profile={profile} setProfile={setProfile} profileLoading={profileLoading} profileMessage={profileMessage} error={error} saveProfile={saveProfile} onBack={() => window.history.back()} />} />}
          </Routes>
        </div>
        {/* Login Modal */}
        {showLogin && (
          <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'}}>
            <div style={{background: '#fff', borderRadius: '8px', padding: '32px', minWidth: '320px', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 2px 16px rgba(0,0,0,0.15)', position: 'relative'}}>
              <button
                onClick={() => setShowLogin(false)}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: '#fff',
                  border: '2px solid #133250',
                  color: '#133250',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  fontSize: '2em',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(19,50,80,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s, color 0.2s'
                }}
                onMouseOver={e => {
                  e.target.style.background = '#133250';
                  e.target.style.color = '#fff';
                }}
                onMouseOut={e => {
                  e.target.style.background = '#fff';
                  e.target.style.color = '#133250';
                }}
              >
                ×
              </button>
              <LoginPage
                onSignIn={(email, password) => {
                  setEmail(email);
                  setPassword(password);
                  signIn();
                }}
                onSignUp={async (email, password, username) => {
                  await signUp(email, password, username);
                }}
                error={error}
                success={success}
              />
            </div>
          </div>
        )}
      </div>
    </Router>
  );
}

export default App;